import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';

import { Confirm, Icons, Modal } from '@components/common';
import Table from '@components/common/Table';
import OrganisationForm from '@components/ModalComponents/OrganisationForm';

import { useMutateBlockUnblockOrganisation } from '@hooks/organisation/useMutateBlockUnblockOrganisation';
import { useMutateCreateOrganisation } from '@hooks/organisation/useMutateCreateOrganisation';
import { useMutateRemoveOrganisations } from '@hooks/organisation/useMutateRemoveOrganisations';
import { useMutateRemoveSingleOrganisation } from '@hooks/organisation/useMutateRemoveSingleOrganisation';
import { useQueryOrganisationsByFilter } from '@hooks/organisation/useQueryOrganisationsByFilter';
import { useQueryOrganisationTypes } from '@hooks/organisation/useQueryOrganisationTypes';

import { organisations as organisationsSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setIsActive,
	setOrganisationType,
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
} from '@slices/organisations';

import { setFilterUtil } from '@utils/filterUtil';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { MODAL_WIDTH_480, MODAL_WIDTH_772 } from '@constants/modals';
import { addTooltipText, searchTooltipText } from './constants';
import { statusOptions } from '@components/common/Table/constants';

import { getColumns } from './manageOrganisationsColumns';

import styles from './Organisations.module.scss';

const Organisations = () => {
	const dispatch = useAppDispatch();
	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		isActive,
		organisationType,
	} = useAppSelector(organisationsSelector);
	const navigate = useNavigate();

	const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
	const [isOrganisationFormModal, setIsOrganisationFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);

	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [singleIdForRemove, setSingleIdForRemove] = useState<string | null>(null);
	const [selectedStatus, setSelectedStatus] = useState<TSelectOption | null>(null);
	const [selectedOrganisationType, setSelectedOrganisationType] = useState<TSelectOption | null>(null);

	const onSearchHandler = (searchValue: string) => dispatch(setSearchValue({ searchValue }));

	const toggleOrganisationModal = () => setIsOrganisationFormModal(prevState => !prevState);

	const closeModals = () => setIsOrganisationFormModal(false);

	const openDeleteConfirm = () => setIsShowDeleteConfirm(prevState => !prevState);

	const closeDeleteConfirm = () => {
		setIsShowDeleteConfirm(false);
		setSingleIdForRemove(null);
	};

	const {
		organisations,
		isLoadingOrganisations,
		countOfRecords,
	} = useQueryOrganisationsByFilter({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		isActive,
		organisationType,
	});

	const { handleCreateOrganisation, isLoadingCreateOrganisation } = useMutateCreateOrganisation([closeModals]);
	const { handleDeleteOrganisations, isLoadingDeleteOrganisations } = useMutateRemoveOrganisations();
	const { handleDeleteOrganisation, isLoadingDeleteOrganisation } = useMutateRemoveSingleOrganisation();
	const { handleChangeStatusOrganisation, isUpdatingOrganisationStatus } = useMutateBlockUnblockOrganisation();

	const {
		optionsOrganizationTypes,
		isLoadingOrganisationTypes,
	} = useQueryOrganisationTypes();

	const isLoadingTable = (
		isLoadingOrganisations ||
		isLoadingDeleteOrganisation ||
		isLoadingDeleteOrganisations ||
		isUpdatingOrganisationStatus
	);

	const search: Array<ITableSearch> = [{
		initialValue: searchValue,
		name: 'searchValue',
		handler: onSearchHandler,
		tooltipText: searchTooltipText,
	}];

	const deleteItemsHandler = () => {
		if (singleIdForRemove) {
			handleDeleteOrganisation(singleIdForRemove);
		} else {
			handleDeleteOrganisations(checkedIds as any);
		}

		closeDeleteConfirm();
	};

	const columns = getColumns({
		openDeleteConfirm,
		setSingleIdForRemove,
		navigate,
		handleChangeStatusOrganisation,
	});

	const handleSort = (sortBy: Array<ISortBy>) =>
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedStatus,
			currentValue: isActive,
			setAction: setIsActive,
			fieldName: 'isActive',
			dispatch,
		});
	}, [dispatch, isActive, selectedStatus]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedOrganisationType,
			currentValue: organisationType,
			setAction: setOrganisationType,
			fieldName: 'organisationType',
			dispatch,
		});
	}, [dispatch, organisationType, selectedOrganisationType]);

	const filters: Array<TTableFilter> = [
		{
			name: 'status',
			placeholder: 'Status',
			option: selectedStatus,
			options: statusOptions,
			minWidth: '200px',
			setState: setSelectedStatus,
			defaultValue: isActive,
		},
		{
			name: 'organisationType',
			placeholder: 'Organisation type',
			option: selectedOrganisationType,
			options: optionsOrganizationTypes,
			minWidth: '200px',
			setState: setSelectedOrganisationType,
			defaultValue: organisationType,
		},
	];

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const pageHandler = (page: number) => dispatch(setPage({ page }));

	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	return (
		<div className={styles.organisationsContainer}>
			<Modal
				isOpen={isShowDeleteConfirm}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={deleteItemsHandler}
					question='Are you sure you want to delete the Organisation(s)?'
				>
					<>
						All Users, assets, and supporting assets data
						<br />
						of the Organisation will be deleted.
					</>
				</Confirm>
			</Modal>

			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isOrganisationFormModal}
				onClose={closeModals}
				title='Add organisation'
				width={MODAL_WIDTH_772}
			>
				<OrganisationForm
					closeModals={closeModals}
					isLoadingForm={isLoadingCreateOrganisation}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitHandler={handleCreateOrganisation}
					toggleOrganisationModal={toggleOrganisationModal}
				/>
			</Modal>
			<div className={styles.pageTitle}>
				<div className={styles.icon}>{Icons.bag({ isActive: true })}</div>
				<div className={styles.title}>Manage Organisations</div>
			</div>
			<div className={styles.organisationsTableContainer}>
				<Table
					addTooltipText={addTooltipText}
					columns={columns as ColumnDef<any>[]}
					data={organisations}
					filters={filters}
					isCreatable
					isLoading={isLoadingTable || isLoadingOrganisationTypes || isLoadingOrganisations}
					isOpenFilters={!!(isActive || organisationType)}
					isOpenSearch={!!searchValue.length}
					isPinColumns
					pagination={pagination}
					search={search}
					setCheckedIds={setCheckedIds}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1, 2]}
					toggleAddModal={toggleOrganisationModal}
					toggleDeleteModal={openDeleteConfirm}
				/>
			</div>
		</div>
	);
};

export default Organisations;
